import React, { useEffect, useMemo, useState } from "react";

/* ================= TYPES ================= */

interface Product {
  name: string;
  category?: string;
  image?: string;
}

interface OrderItem {
  product: Product;
  quantity: number;
  price: number;
}

interface Order {
  id: number;
  customer_name: string;
  total_amount: number;
  order_status: string;
  ordered_at: string;
  items: OrderItem[];
}

const STATUS_OPTIONS = [
  "Pending",
  "Processing",
  "Shipped",
  "Delivered",
  "Cancelled",
];

/* ================= STATUS BADGE ================= */

const StatusBadge = ({ status }: { status: string }) => {
  const colors: Record<string, string> = {
    Pending: "bg-yellow-100 text-yellow-800",
    Processing: "bg-blue-100 text-blue-800",
    Shipped: "bg-purple-100 text-purple-800",
    Delivered: "bg-green-100 text-green-800",
    Cancelled: "bg-red-100 text-red-800",
  };

  return (
    <span
      className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
        colors[status] || "bg-gray-100 text-gray-800"
      }`}
    >
      {status}
    </span>
  );
};

/* ================= SUMMARY CARD ================= */

const SummaryCard = ({
  title,
  value,
  subtitle,
}: {
  title: string;
  value: string;
  subtitle?: string;
}) => (
  <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
    <p className="text-sm text-gray-500">{title}</p>
    <p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
    {subtitle && (
      <p className="mt-1 text-xs text-gray-400">{subtitle}</p>
    )}
  </div>
);

/* ================= COMPONENT ================= */

const PAGE_SIZE = 10;

const AdminDashboard: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [expandedOrder, setExpandedOrder] = useState<number | null>(null);
  const [page, setPage] = useState(1);

  const API_URL = process.env.REACT_APP_BASE_URL;

  /* ========== LOAD ORDERS ========== */

  useEffect(() => {
    const loadOrders = async () => {
      try {
        const res = await fetch(`${API_URL}/orders`, {
          credentials: "include",
        });
        const data = await res.json();

        const normalized = data.map((o: any) => ({
          ...o,
          total_amount: Number(o.total_amount),
        }));

        setOrders(normalized);
      } catch (error) {
        console.error("Failed to load orders:", error);
      }
    };

    loadOrders();
  }, [API_URL]);

  /* ========== METRICS ========== */

  const metrics = useMemo(() => {
    const totalOrders = orders.length;
    const totalRevenue = orders.reduce(
      (sum, o) => sum + o.total_amount,
      0
    );
    const pending = orders.filter(
      (o) => o.order_status === "Pending"
    ).length;
    const delivered = orders.filter(
      (o) => o.order_status === "Delivered"
    ).length;

    return {
      totalOrders,
      totalRevenue,
      pending,
      delivered,
    };
  }, [orders]);

  /* ========== PAGINATION ========== */

  const totalPages = Math.ceil(orders.length / PAGE_SIZE);
  const paginatedOrders = orders.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  /* ================= RENDER ================= */

  return (
    <div className="min-h-screen bg-gray-50 p-6 space-y-6">
      {/* ================= HEADER ================= */}
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">
          Dashboard
        </h2>
        <p className="text-sm text-gray-500">
          Overview of orders and performance
        </p>
      </div>

      {/* ================= SUMMARY ================= */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          title="Total Orders"
          value={metrics.totalOrders.toString()}
        />
        <SummaryCard
          title="Total Revenue"
          value={`UGX ${metrics.totalRevenue.toLocaleString()}`}
        />
        <SummaryCard
          title="Pending Orders"
          value={metrics.pending.toString()}
        />
        <SummaryCard
          title="Delivered Orders"
          value={metrics.delivered.toString()}
        />
      </div>

      {/* ================= TABLE ================= */}
      <div className="overflow-x-auto bg-white rounded-lg shadow border border-gray-200">
        <table className="min-w-full border-collapse text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left">Order ID</th>
              <th className="px-4 py-3 text-left">Customer</th>
              <th className="px-4 py-3 text-left">Total</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>

          <tbody>
            {paginatedOrders.length ? (
              paginatedOrders.map((order) => (
                <React.Fragment key={order.id}>
                  <tr className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3">{order.id}</td>
                    <td className="px-4 py-3">
                      {order.customer_name}
                    </td>
                    <td className="px-4 py-3 font-medium">
                      UGX {order.total_amount.toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={order.order_status} />
                    </td>
                    <td className="px-4 py-3">
                      {new Date(
                        order.ordered_at
                      ).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() =>
                          setExpandedOrder(
                            expandedOrder === order.id
                              ? null
                              : order.id
                          )
                        }
                        className="text-blue-600 text-xs hover:underline"
                      >
                        {expandedOrder === order.id
                          ? "Hide Items"
                          : "View Items"}
                      </button>
                    </td>
                  </tr>

                  {expandedOrder === order.id && (
                    <tr className="bg-gray-50">
                      <td colSpan={6} className="px-6 py-4">
                        <div className="space-y-3">
                          {order.items.map((item, idx) => (
                            <div
                              key={idx}
                              className="flex justify-between items-center rounded border bg-white p-3"
                            >
                              <div>
                                <p className="font-medium">
                                  {item.product.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {item.product.category ||
                                    "Uncategorized"}
                                </p>
                              </div>
                              <div className="text-xs text-right">
                                <p>Qty: {item.quantity}</p>
                                <p>
                                  UGX{" "}
                                  {item.price.toLocaleString()}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td
                  colSpan={6}
                  className="py-10 text-center text-gray-500"
                >
                  No orders found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ================= PAGINATION ================= */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Page {page} of {totalPages}
        </p>
        <div className="flex gap-2">
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className="rounded border px-3 py-1 text-sm disabled:opacity-40"
          >
            Previous
          </button>
          <button
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="rounded border px-3 py-1 text-sm disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
